/**
 * Offline download queue: ONE file at a time, resumable (files already stored with the same etag are
 * skipped), retries with backoff, Screen Wake Lock while running, global progress in the store.
 *
 * Storage reserve: a file is only downloaded while the origin keeps at least storageReserve() free
 * after it (navigator.storage.estimate()); otherwise the queue stops with 'storage_full' BEFORE
 * filling the disk, so the small writes that matter (annotations, the outbox, the query cache)
 * still have room.
 *
 * Every file is validated before it is stored (validate.ts): 200, X-KGR, never HTML, the served
 * Content-Type, Content-Length + ETag equal to the manifest — and the stored byte count is checked
 * after cache.put(). A 403/404 evicts the entry and re-syncs the manifest (it is stale).
 */
import { ApiFailure, apiRaw, isNetworkFailure } from '../api/client';
import { offlineOp, openContent } from './db';
import { currentPlans, data, isCurrent, missingItems, publish, publishSoon } from './store';
import type { ItemRow, PackRow, PlannedItem } from './types';
import { validateMediaResponse, type InvalidReason } from './validate';
import { holdWakeLock } from './wakeLock';

/** Error codes stored on a pack (translated by the UI: offline.error.<code>). */
export type PackErrorCode = 'network' | 'storage_full' | 'unsupported' | 'server' | 'invalid';

class InvalidResponse extends Error {
  constructor(public readonly reason: InvalidReason) {
    super(`invalid response: ${reason}`);
  }
}
/** A body stream that broke mid-transfer (retryable like a NetworkFailure). */
class BrokenStream extends Error {}
class StorageFull extends Error {}
class Unsupported extends Error {}

export interface DownloaderHooks {
  /** The manifest is stale (403/404, etag/size mismatch): fetch it again and re-plan. */
  resync(): Promise<void>;
  /** Removes a URL from every cache and from the items table. */
  evict(urls: string[]): Promise<void>;
}

let hooks: DownloaderHooks | null = null;
export function setDownloaderHooks(h: DownloaderHooks): void {
  hooks = h;
}

const MB = 1024 * 1024;

/** Space packs must leave free on the origin: max(200 MB, 5 % of the quota). */
export function storageReserve(quota: number): number {
  return Math.max(200 * MB, Math.ceil(quota * 0.05));
}

/** Room for `bytes` more on top of the reserve? null when the estimate is unusable (unknown: go ahead). */
export function roomForBytes(estimate: { quota?: number; usage?: number } | null | undefined, bytes: number): boolean | null {
  const quota = estimate?.quota;
  const usage = estimate?.usage;
  if (typeof quota !== 'number' || typeof usage !== 'number' || !Number.isFinite(quota) || !Number.isFinite(usage) || quota <= 0) return null;
  return quota - usage >= bytes + storageReserve(quota);
}

/** Room on this device for a download of `bytes` (null: the browser gives no usable estimate). */
export async function roomFor(bytes: number): Promise<boolean | null> {
  try {
    if (typeof navigator === 'undefined' || !navigator.storage?.estimate) return null;
    return roomForBytes(await navigator.storage.estimate(), bytes);
  } catch {
    return null;
  }
}

const MAX_ATTEMPTS = 6;
const BACKOFF_MS = [2_000, 5_000, 15_000, 30_000, 60_000];

/** The queue loop in progress, tagged with the session (generation) it works for. */
let running: { gen: number; p: Promise<void> } | null = null;
/** Set when cache.put() refused a streamed body once (old WebKit): later files are buffered first. */
let streamPutUnsupported = false;

async function readAll(body: ReadableStream<Uint8Array<ArrayBuffer>>, onBytes: (n: number) => void): Promise<Blob> {
  const reader = body.getReader();
  const parts: BlobPart[] = [];
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    parts.push(value);
    onBytes(value.byteLength);
  }
  return new Blob(parts);
}
let current: AbortController | null = null;
/** URL being downloaded/stored right now (the cache sweep must not delete it before it is recorded). */
let currentUrl: string | null = null;
let wakeup: (() => void) | null = null;

export function busyUrl(): string | null {
  return currentUrl;
}

function aborted(): Error {
  return new DOMException('aborted', 'AbortError');
}

/** Download timeout scaled to size (assumes ≥ 25 kB/s on bad choir-hall Wi-Fi). */
export function downloadTimeoutMs(bytes: number): number {
  return 60_000 + Math.ceil(bytes / 25_000) * 1000;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    const t = setTimeout(done, ms);
    function done() {
      clearTimeout(t);
      if (wakeup === done) wakeup = null;
      resolve();
    }
    wakeup = done;
  });
}

function waitForOnline(): Promise<void> {
  return new Promise((resolve) => {
    const done = () => {
      window.removeEventListener('online', done);
      if (wakeup === done) wakeup = null;
      resolve();
    };
    window.addEventListener('online', done);
    wakeup = done;
  });
}

/** Wakes a queue that waits for the network / a retry (e.g. 'online', manual retry, stop). */
export function nudgeQueue(): void {
  wakeup?.();
}

/** Aborts the file in flight and stops the queue (logout / user switch / pack removed). */
export function abortCurrentDownload(): void {
  current?.abort();
  nudgeQueue();
}

/** Aborts the file in flight when no pack plans it any more (e.g. its pack was just removed). */
export function abortUnwantedDownload(): void {
  if (!current || !currentUrl) return;
  if (packsWanting(currentUrl, currentPlans()).some((id) => data.packs.has(id))) return;
  abortCurrentDownload();
}

/** Packs that still need work, oldest first (error packs wait for a retry). */
function nextWork(skip: ReadonlySet<string>): { pack: PackRow; item: PlannedItem } | null {
  const packs = [...data.packs.values()].filter((p) => p.status !== 'complete' && p.status !== 'error').sort((a, b) => a.createdAt - b.createdAt);
  for (const pack of packs) {
    const item = missingItems(pack.id).find((it) => !skip.has(it.url));
    if (item) return { pack, item };
  }
  return null;
}

/** Ids of every pack whose plan contains this URL (shared files are stored once). */
function packsWanting(url: string, plans: Map<string, PlannedItem[] | null>): string[] {
  const out: string[] = [];
  for (const [id, plan] of plans) if (plan?.some((p) => p.url === url)) out.push(id);
  return out;
}

async function storeItem(gen: number, item: PlannedItem, packIds: string[], content: Cache): Promise<void> {
  if (!isCurrent(gen)) return;
  // Packs removed while the file was downloading no longer own it; nobody left → do not keep it.
  const owners = packIds.filter((id) => data.packs.has(id));
  if (owners.length === 0) {
    await content.delete(item.url).catch(() => false);
    await offlineOp((db) => db.items.delete(item.url));
    data.items.delete(item.url);
    return;
  }
  const row: ItemRow = { ...item, packIds: owners, cachedAt: Date.now() };
  await offlineOp((db) => db.items.put(row));
  if (!isCurrent(gen)) return;
  data.items.set(item.url, row);
}

/** Downloads one file into the content cache (or just links it when it is already stored). */
async function downloadItem(gen: number, item: PlannedItem, packIds: string[]): Promise<void> {
  const content = await openContent();
  if (!content) throw new Unsupported();

  // Resumable: a file already in the cache with the same etag only needs its pack links.
  const known = data.items.get(item.url);
  if (known && known.etag === item.etag && known.size === item.size && (await content.match(item.url))) {
    await storeItem(gen, item, [...new Set([...known.packIds, ...packIds])], content);
    return;
  }

  // Never fill the disk: stop with 'storage_full' while the reserve would not be left free.
  if ((await roomFor(item.size)) === false) throw new StorageFull();

  const ctrl = new AbortController();
  current = ctrl;
  currentUrl = item.url;
  data.inflightBytes = 0;
  try {
    const res = await apiRaw(item.url.replace(/^\/api/, ''), { signal: ctrl.signal, timeoutMs: downloadTimeoutMs(item.size) });
    const bad = validateMediaResponse(res, item);
    if (bad || !res.body) {
      await res.body?.cancel().catch(() => undefined);
      throw new InvalidResponse(bad ?? 'length');
    }
    let counted = 0;
    const onBytes = (n: number) => {
      counted += n;
      data.inflightBytes = counted;
      publishSoon();
    };
    const headers = new Headers({ 'content-type': item.mime, etag: `"${item.etag}"`, 'content-length': String(item.size), 'x-kgr': '1' });
    if (!isCurrent(gen)) {
      ctrl.abort();
      return;
    }
    try {
      if (streamPutUnsupported) {
        // Fallback for engines that cannot store a streamed body: buffer the file, then store it.
        const blob = await readAll(res.body, onBytes);
        await content.put(item.url, new Response(blob, { status: 200, headers }));
      } else {
        const counter = new TransformStream<Uint8Array, Uint8Array>({
          transform(chunk, out) {
            onBytes(chunk.byteLength);
            out.enqueue(chunk);
          },
        });
        await content.put(item.url, new Response(res.body.pipeThrough(counter), { status: 200, headers }));
      }
    } catch (e) {
      const name = (e as Error)?.name;
      if (name === 'QuotaExceededError') throw new StorageFull();
      if (ctrl.signal.aborted) throw e;
      // Refused before a single byte was read: this engine cannot store streams → buffer from now on.
      if (!streamPutUnsupported && counted === 0 && (name === 'NotSupportedError' || name === 'TypeError')) streamPutUnsupported = true;
      // A broken or timed-out body stream surfaces from cache.put(): retry it like a network failure.
      throw new BrokenStream((e as Error)?.message);
    }
    if (!isCurrent(gen)) {
      await content.delete(item.url).catch(() => false);
      return;
    }
    if (counted !== item.size) {
      await content.delete(item.url).catch(() => false);
      throw new InvalidResponse('length');
    }
    await storeItem(gen, item, packIds, content);
  } catch (e) {
    // Aborted on purpose: engines report it differently (AbortError, TypeError from cache.put(), a
    // NetworkFailure) — the loop must see an AbortError, never a failure to record on the pack.
    if (ctrl.signal.aborted) throw aborted();
    throw e;
  } finally {
    if (current === ctrl) {
      current = null;
      currentUrl = null;
    }
    data.inflightBytes = 0;
  }
}

async function setPackStatus(gen: number, packId: string, status: PackRow['status'], error: string | null): Promise<void> {
  const p = data.packs.get(packId);
  if (!p || !isCurrent(gen) || (p.status === status && p.error === error)) return;
  const next = { ...p, status, error };
  data.packs.set(packId, next);
  await offlineOp((db) => db.packs.put(next));
  publish();
}

/** Marks every pack that no longer misses anything as complete. */
async function settleCompletePacks(gen: number): Promise<void> {
  for (const p of [...data.packs.values()]) {
    if (p.status !== 'complete' && missingItems(p.id).length === 0 && isCurrent(gen)) await setPackStatus(gen, p.id, 'complete', null);
  }
}

async function loop(gen: number): Promise<void> {
  const skip = new Set<string>(); // URLs that failed for good in this run
  let attempts = 0;
  let resynced = false;
  holdWakeLock(true);
  try {
    while (isCurrent(gen)) {
      await settleCompletePacks(gen);
      const work = nextWork(skip);
      if (!work) {
        // Files that failed for good even after a fresh manifest: their packs wait for a retry.
        for (const p of [...data.packs.values()]) {
          if (p.status !== 'complete' && p.status !== 'error' && missingItems(p.id).some((it) => skip.has(it.url))) {
            await setPackStatus(gen, p.id, 'error', 'invalid');
          }
        }
        break;
      }
      data.activePackId = work.pack.id;
      if (!navigator.onLine) {
        data.waiting = 'network';
        publish();
        holdWakeLock(false);
        await waitForOnline();
        holdWakeLock(true);
        data.waiting = null;
        continue;
      }
      data.waiting = null;
      publish();
      try {
        await downloadItem(gen, work.item, packsWanting(work.item.url, currentPlans()));
        attempts = 0;
        publish();
      } catch (e) {
        if (!isCurrent(gen)) break;
        if ((e as Error)?.name === 'AbortError') {
          // Aborted on purpose (pack removed / reset): pick the next piece of work.
          continue;
        }
        if (e instanceof ApiFailure && (e.status === 403 || e.status === 404)) {
          // No longer visible / not the current revision: forget it and re-plan from a fresh manifest.
          skip.add(work.item.url);
          await hooks?.evict([work.item.url]);
          if (!resynced && hooks) {
            resynced = true;
            await hooks.resync();
          } else {
            await setPackStatus(gen, work.pack.id, 'error', 'invalid');
          }
          continue;
        }
        if (e instanceof InvalidResponse && (e.reason === 'etag' || e.reason === 'length' || e.reason === 'type')) {
          skip.add(work.item.url);
          if (!resynced && hooks) {
            resynced = true;
            await hooks.resync();
          } else {
            await setPackStatus(gen, work.pack.id, 'error', 'invalid');
          }
          continue;
        }
        if (e instanceof StorageFull) {
          for (const p of data.packs.values()) if (p.status !== 'complete') await setPackStatus(gen, p.id, 'error', 'storage_full');
          break;
        }
        if (e instanceof Unsupported) {
          for (const p of data.packs.values()) if (p.status !== 'complete') await setPackStatus(gen, p.id, 'error', 'unsupported');
          break;
        }
        const retryable =
          isNetworkFailure(e) ||
          e instanceof BrokenStream ||
          (e instanceof InvalidResponse && (e.reason === 'contract' || e.reason === 'html' || e.reason === 'status')) ||
          (e instanceof ApiFailure && (e.status >= 500 || e.status === 408 || e.status === 429));
        if (retryable && attempts < MAX_ATTEMPTS) {
          attempts++;
          data.waiting = 'retry';
          publish();
          await sleep(BACKOFF_MS[Math.min(attempts, BACKOFF_MS.length) - 1]!);
          data.waiting = null;
          continue;
        }
        attempts = 0;
        await setPackStatus(gen, work.pack.id, 'error', retryable ? 'network' : e instanceof ApiFailure ? 'server' : 'invalid');
      }
    }
  } finally {
    if (isCurrent(gen)) {
      data.activePackId = null;
      data.waiting = null;
      data.inflightBytes = 0;
      publish();
    }
    holdWakeLock(false);
  }
}

/** Starts the queue if it is not running; resolves when it is idle. */
export function runQueue(): Promise<void> {
  if (running) {
    // A loop of an earlier session (logout / user switch) is still winding down: start after it.
    return running.gen === data.generation ? running.p : running.p.then(() => runQueue());
  }
  const gen = data.generation;
  const p: Promise<void> = loop(gen).then(
    () => {
      if (running?.p === p) running = null;
      // Work added while the last iteration was finishing (for whichever session is current now): go again.
      if (nextWork(new Set()) && navigator.onLine) void runQueue();
    },
    (e: unknown) => {
      if (running?.p === p) running = null;
      console.warn('offline download queue stopped', e); // e.g. IndexedDB gone: the next start retries
    },
  );
  running = { gen, p };
  return p;
}

export function isQueueRunning(): boolean {
  return running !== null;
}
